import './index.scss';

import { UseQueryResult } from '@tanstack/react-query';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { showToast } from '@/src/globalFunction';

import AccessList from './AccessList/AccessList';
import Search from './Actions/Search/Search';

import { GetLibraryMembersResponse } from '../../../api/getLibraryMembers';
import { LibraryUserAccessDTO } from '../../../types';

export type MemberActionInput = {
  memberId: number;
};

type ManageAccessListProps = {
  onClose: () => void;
  onAdd?: (data: MemberActionInput) => Promise<unknown>;
  onDelete?: (data: MemberActionInput) => Promise<unknown>;
  onSaveMembers?: (
    membersToAdd: LibraryUserAccessDTO[],
    membersToDelete: LibraryUserAccessDTO[]
  ) => void;
  libraryMembersQuery: UseQueryResult<GetLibraryMembersResponse>;
  onSaved?: () => void;
  shouldCloseOnSave?: boolean;
};

const ManageAccessList: React.FC<ManageAccessListProps> = ({
  onClose,
  onSaveMembers,
  libraryMembersQuery,
  onAdd = null,
  onDelete = null,
  onSaved = null,
  shouldCloseOnSave = true,
}) => {
  const dispatch = useDispatch();
  const { projectId } = useParams<{ projectId: string }>();
  const [isClose, setIsClose] = useState(false);
  const [members, setMembers] = useState([]);
  const [searchOptions, setSearchOptions] = useState([]);
  const membersToDelete = members.filter((m) => m.access === 'remove' && !m.isNew);
  const membersToAdd = members.filter((m) => m.access !== 'remove' && m.isNew);
  const hasChanges = membersToDelete.length > 0 || membersToAdd.length > 0;

  useEffect(() => {
    if (!libraryMembersQuery.data) {
      return;
    }

    setMembers(
      libraryMembersQuery.data.data['ordered-allowed-users'].map((u) => {
        return {
          ...u,
          id: u.id === null ? u['first-name'] + u['last-name'] : u.id,
          access: 'editor',
        };
      })
    );

    setSearchOptions(
      libraryMembersQuery.data.data['ordered-members']
        .filter((m) => m.editable)
        .map((u) => {
          return {
            ...u,
            id: u.id === null ? u['first-name'] + u['last-name'] : u.id,
            access: 'editor',
            value: u.id === null ? u['first-name'] + u['last-name'] : u.id,
            label: `${u['first-name']} ${u['last-name']}`,
            others: {
              ...u,
              access: 'editor',
            },
          };
        })
    );
  }, [libraryMembersQuery.data]);

  const handleClose = () => {
    setIsClose(true);
    setMembers([]);
    dispatch({ type: 'UPDATE_FILES_ACTION_ITEM', action: '' });
    dispatch({ type: 'UPDATE_FILES_SELECTED', selected: {} });
    onClose();
  };

  const handleSelect = (value) => {
    setMembers([...members, { ...value, isNew: true }]);
  };

  const handleSelectAction = (m, a) => {
    if (m.isNew && a === 'remove') {
      setMembers(members.filter((memberInLoop) => memberInLoop.id !== m.id));
    } else {
      setMembers(
        members.map((member) => {
          if (member.id === m.id) {
            return {
              ...member,
              access: a,
              hasChanges: true,
            };
          }
          return member;
        })
      );
    }
  };

  const handleSave = async () => {
    if (membersToDelete.length < 1 && membersToAdd.length < 1) {
      return;
    }

    if (onSaveMembers) {
      onSaveMembers(membersToAdd, membersToDelete);
      return;
    }

    if (!onAdd && !onDelete) {
      throw new Error(
        'onAdd and onDelete must be provided for ManageAccessList if no onSaveMembers prop is sent'
      );
    }

    const addPromises = membersToAdd.map((member) =>
      onAdd({
        memberId: member.id,
      })
    );
    const deletePromises = membersToDelete.map((member) =>
      onDelete({
        memberId: member.id,
      })
    );

    await Promise.all([...addPromises, ...deletePromises]);

    showToast('success', `User Access successfully updated!`);

    if (onSaved) {
      onSaved();
    }

    if (shouldCloseOnSave) {
      handleClose();
    }
  };

  return (
    <div
      className="modal fade show in confirm-modal bg-overlay"
      data-backdrop="static"
      data-keyboard="false"
      id="manageAccessList"
    >
      <div className="modal-dialog" role="document">
        <div className="modal-content">
          <div className="modal-header bb pd20 pdl40 pdr40">
            <div className="row">
              <div className="col-lg-10 col-md-10 col-sm-10">
                <p className="text-size18 brndnTxt-m mb0 mt5">Manage Folder Access</p>
              </div>
              <div className="col-lg-2 col-md-2 col-sm-2">
                <a className="text-right text-black" onClick={handleClose}>
                  <i className="material-icons text-black text-size22">close</i>
                </a>
              </div>
            </div>
          </div>
          <div className="modal-body pt10 pb10">
            <div className="memberSearch">
              <i className="material-icons searchIcon">search</i>
              <Search
                projectId={projectId}
                isClose={isClose}
                onSelect={handleSelect}
                avatarBaseUrl={`/api/file/profile_pictures/`}
                exclude={members.map((m) => m.id)}
                options={searchOptions}
              />
            </div>
            <div className="memberList">
              {members.map((m, index) => (
                <AccessList key={m.id || index} member={m} onSelect={handleSelectAction} />
              ))}
            </div>
          </div>
          <div className="modal-footer bt">
            <div className="row">
              <div className="col-lg-4 col-md-4 col-sm-4">
                <button type="button" className="btn btnCancel ml0" onClick={handleClose}>
                  Cancel
                </button>
              </div>
              <div className="col-lg-8 col-md-8 col-sm-8 text-align-right">
                <button
                  type="button"
                  className="btn btnSave"
                  onClick={handleSave}
                  disabled={!hasChanges}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageAccessList;
